import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";

import DocPage from "../components/DocPage";

// Questions are stored in sentence case and answers in markdown. Both used to
// be shouted in full caps and injected as raw HTML through
// dangerouslySetInnerHTML; caps is measurably slower to read at length, and the
// HTML was only ever carrying <strong> and bullet characters, which markdown
// does without handing an injection surface to a content edit.
const cbqSections = [
  {
    title: "General",
    questions: [
      [
        "What is BLEEP?",
        `Bleep is a web and mobile app that facilitates enables consumers to report suspected counterfeit or substandard products to enable verification and remedial action by regulators, brands and other market actors.`,
      ],
      [
        "How does it work?",
        `**Bleep operates as a three-tiered system:** RVR, Report, Verify & Remedial Action.

**Report:** Upload a photo, video or voice clip with a short description of any suspicious product or service and submit a report via the app.

**Verify:** Reports are reviewed by brands, regulators, or the Bleep team. Once assessed, they are marked as 'Ongoing' or 'Verified'.

**Remedial Action:** Verified cases may trigger product recalls, seizures, investigations or enforcement. You'll receive updates at every step.`,
      ],
      [
        "Who can use BLEEP?",
        `Anyone can use Bleep. Whether you're a consumer, manufacturer, importer, distributor, retailer, or regulator. The platform is open to all users who want to check product authenticity, report suspicious items, or monitor the supply chain.`,
      ],
      [
        "Is BLEEP free to use?",
        `Bleep is free to use for consumers. Businesses pay based on chosen service packages and the number of security tags needed.`,
      ],
      [
        "Does BLEEP work offline?",
        `No. At the moment, Bleep requires an internet connection to function.`,
      ],
    ],
  },
  {
    title: "BLEEP Security Tag (BleST)",
    questions: [
      [
        "What is the BLEEP Security Tag (BleST)?",
        `It is a unique product that is tamper-proof, non-cloneable security label placed on verified products. It allows consumers verify authenticity, in real-time or before purchase.

For businesses, helps brands including manufacturers, importers/distributors, retailers and regulators to track product movement and detect counterfeiting attempts within their supply chain.`,
      ],
      [
        "Who is eligible to use the BLEEP Security Tags?",
        `Bleep security tags are particularly ideal for any stakeholder in the supply chain aiming to prevent counterfeiting, track pilferage, enforce territorial sales boundaries and enable geo-tagged authenticity scans.

Eligibility varies from:

- **Manufacturers** looking to protect their brand (validated manufacturers)
- **Distributors / Importers** ensuring supply chain integrity (validated on authorization and geographical jurisdiction)
- **Regulators** monitoring product movement
- **End-consumers** seeking product verification`,
      ],
      [
        "How do I use the security tag feature?",
        `To use it, tap on the "Iris: Verify Product" button in the app. This will open your camera. Position the camera over the tag to scan and check if the product is genuine.`,
      ],
      [
        "What happens when I scan the tag?",
        `The app will instantly show the product's verification status, letting you know whether it's genuine or counterfeit.`,
      ],
      [
        "What do I do if the product is flagged as counterfeit?",
        `You will have avoided purchasing a counterfeit product. You can then report the counterfeit product through the App to help Bleep investigate further and notify relevant stakeholders.`,
      ],
      [
        "What if the scan does not return any results?",
        `If no information is available, the app will prompt you to submit the product details manually for verification.`,
      ],
      [
        "Can I scan products without creating an account?",
        `Yes, scanning is available without an account. However, in order to verify, track and report suspected counterfeit products more efficiently, you need to have an account.`,
      ],
      [
        "What types of codes can the app scan?",
        `Only official Bleep Security Tags provided by the Bleep team to partner brands can be scanned through the app.`,
      ],
      [
        "What if the camera is not scanning the security tag correctly?",
        `Make sure you're in good lighting, clean your lens, and hold the camera steady. If issues continue, you can manually submit the product details.`,
      ],
      [
        "Is the security tag feature available offline?",
        `No, it requires an Internet connection to collect real-time verification results from the database.`,
      ],
      [
        "Can I scan a product more than once?",
        `Yes. You can scan a product multiple times if needed. However, if it still seems suspicious, submit a report.`,
      ],
      [
        "What kind of products can I verify with the sticker feature?",
        `You can verify a wide range of products, including electronics, cosmetics, pharmaceuticals, and more, as long as they have the security tag.`,
      ],
      [
        "What should I do if the security tag feature fails to work?",
        `Restart the app, and ensure you have a stable internet connection. If the issue persists, contact our support team through the in-app "Contact us" form, reach out on our social media pages @bleep_app_ or email **info@bleep.org** for assistance.`,
      ],
      [
        "Does using the BLEEP Security Tag compromise my personal data privacy?",
        `No, the use of the security tag is primarily intended to ensure product authenticity, traceability and supply chain security.`,
      ],
      [
        "Can the BLEEP Security Tag be used to track me personally?",
        `No, the tag itself does not contain or transmit personal information. Tracking is limited to the product's movement within the supply chain.`,
      ],
    ],
  },
  {
    title: "For consumers",
    questions: [
      [
        "How can I use BLEEP to report or verify a product?",
        `- Download the app from the Google Play Store (or Apple App Store once available)
- Register with your phone number or email
- Tap "Report Now," or scan a BleST tag and follow the prompts to validate the product.`,
      ],
      [
        "What information do I need to provide to use the app?",
        `A valid email or phone number (for a one-time password) and details or photos of the product.`,
      ],
      [
        "What types of products can I report on BLEEP?",
        `You can report any type of product you suspect to be counterfeit, in any sector: Pharmaceuticals, Electronics, Organizations, Agriculture, Currencies, Government, Media, Beverages, Cosmetics, Education, Motor industry, Petroleum, Construction, etc.`,
      ],
      [
        "What happens after I submit a report?",
        `Once submitted, your report will be reviewed by our team and relevant stakeholders. You'll then receive app notifications about verification status and any actions taken.`,
      ],
      [
        "Can I follow up on reports?",
        `Yes. Tap on "Follow" on any report to get real-time updates and comments from other users.`,
      ],
      [
        "Can I edit or delete a report after submitting it?",
        `Yes, you can edit/delete your report within five (5) minutes of submission, after which it cannot be removed.`,
      ],
      [
        "Can I share reports on social media?",
        `Yes, Bleep has a sharing option that allows you to share reports directly on social media platforms to raise awareness about potential counterfeit products.`,
      ],
      [
        "How do I disable or suspend my account?",
        `If you wish to disable or suspend your account, please contact our support team via the app or email **info@bleep.org** for assistance.`,
      ],
      [
        "What should I do if I encounter a technical issue with BLEEP?",
        `If you experience any technical issues, contact our support team through the "Help & Support" feature within the app, social media accounts @bleep_app_ or by emailing **info@bleep.ug**.`,
      ],
      [
        "Why should I use BLEEP?",
        `- Stay safe from fake products
- Help protect your community
- Access real-time product history in your hand`,
      ],
    ],
  },
  {
    title: "For brands and manufacturers",
    questions: [
      [
        "How does BLEEP protect my brand?",
        `By issuing encrypted, tamper-proof and non-clonable tags and providing a reporting platform that flags any suspicious product right at the source.`,
      ],
      [
        "How do I integrate my products into BLEEP?",
        `After onboarding, you'll get a custom dashboard where you upload product details, order Bleep security tags, calibrate the series of the tags to product categories, and train your team via our setup support.`,
      ],
      [
        "What data and analytics are available on BLEEP?",
        `Dashboards show scan frequency, geolocation heatmaps, counterfeit alerts, and consumer report trends.`,
      ],
      [
        "What are the costs of using BLEEP for my brand?",
        `Pricing depends on volume of tags, additional value-added services/features, and support level. Custom quotes are provided upon request.`,
      ],
      [
        "What are the benefits of BLEEP for my brand?",
        `- Protects brand reputation and revenue
- Deters counterfeiting and unauthorized sales
- Gains customer trust
- Monitors supply chain in real-time from production to the end-consumer`,
      ],
    ],
  },
  {
    title: "For importers and distributors",
    questions: [
      [
        "How do I verify products I import or distribute?",
        `Scan each security tag in the app or upload batch details to confirm authenticity at every stage.`,
      ],
      [
        "Can BLEEP help track shipments?",
        `Yes. Bleep allows you to track shipments and verify their contents in real time by scanning the Bleep Security Tags on products. With geo-tagging and optional track-and-trace features, you can monitor product movement across the entire supply chain, from origin to shelf.`,
      ],
      [
        "How does BLEEP support compliance with import and export regulations?",
        `Bleep supports compliance by providing digital audit trails, traceability logs, and real-time reports that align with regulatory standards. These tools help businesses and regulators meet transparency and accountability requirements across borders.`,
      ],
      [
        "Does BLEEP help prevent parallel importation?",
        `Yes. Bleep helps protect authorized sales territories by detecting and flagging products that enter a region through unauthorized channels. Its geo-fencing features and territorial settings alert businesses to parallel or illicit importation attempts.`,
      ],
      [
        "Is there training or support for using BLEEP in my business?",
        `Absolutely. Bleep provides full onboarding, technical support, and user training sessions to help your team integrate and use the platform effectively. For assistance, simply contact our support team via **info@bleep.ug**.`,
      ],
      [
        "What are the benefits of using BLEEP?",
        `- Minimize the risk of distributing counterfeit and substandard products
- Real-time tracking simplifies supply chain management and inventory oversight
- Easily meet regulatory and transparency standards
- Strengthens brand reputation by preventing unauthorized sales and counterfeiting`,
      ],
    ],
  },
  {
    title: "For regulators",
    questions: [
      [
        "How can BLEEP help monitor and enforce product authenticity regulations?",
        `Bleep enables real-time verification, geo-tagged scanning, and backward traceability of products. This helps regulators investigate, trace and identify non-compliant or counterfeit goods, allowing them to take swift remedial action.`,
      ],
      [
        "What reporting and data sharing does BLEEP offer for regulatory purposes?",
        `Bleep provides a customizable dashboard with access to product history, compliance logs, scan heatmaps, and report alerts. This data is securely shared with regulatory authorities to support audits, enforcement, and market oversight.`,
      ],
      [
        "Can BLEEP help identify and track counterfeit products in the market?",
        `Yes. Bleep analyzes verification data to quickly flag suspicious activity or anomalies and pinpoints their location, enabling remedial action by regulators and enforcement teams.`,
      ],
      [
        "What personal data does BLEEP collect and use?",
        `To operate effectively, Bleep may collect and process limited personal data such as email addresses and contact numbers. We only collect data that is adequate, relevant, and necessary for a specific lawful purpose. All data is handled in line with data protection laws.`,
      ],
      [
        "Does BLEEP process personal data automatically?",
        `No. Bleep does not perform any automated decision-making or profiling that could result in legal consequences or similarly significant impact on users.`,
      ],
      [
        "Does BLEEP transfer personal data to third parties?",
        `Yes, we share personal data with affiliated third parties, as is deemed necessary for our legitimate business needs or as required or permitted by regulatory requirements. These include:

- Mobile telecommunication companies
- Development Partners
- Courts of Law, tribunals, law enforcement agencies or regulatory bodies
- Audits`,
      ],
      [
        "How does BLEEP secure the personal data of its subjects?",
        `Bleep secures the integrity and confidentiality of the personal data of its subjects by implementing appropriate, reasonable, technical, physical and organizational measures to prevent:

- Accidental loss, damage, or unauthorized destruction of personal data
- Unlawful or unauthorized access to/dissemination of your personal data
- Unlawful or unauthorized processing of personal data`,
      ],
      [
        "Can BLEEP integrate with existing regulatory systems?",
        `Yes. Bleep supports API integrations with regulatory databases and enforcement systems, enabling seamless data exchange, case tracking and compliance monitoring. For integration support, please contact the technical team.`,
      ],
      [
        "What are the benefits of using BLEEP as a regulator?",
        `- Strengthen market surveillance and enforcement
- Secure public health and tax revenues
- Enable evidence-based reports to inform policy and enforcement actions
- Foster consumer confidence through transparent action`,
      ],
    ],
  },
];

const TOTAL_QUESTIONS = cbqSections.reduce(
  (n, section) => n + section.questions.length,
  0,
);

const CBQs = ({ domain }) => {
  const [query, setQuery] = useState("");

  // Fifty-one questions across six audiences is more than anyone scrolls
  // through, and the audience a reader belongs to is not always the section
  // their answer sits in. Filtering matches question and answer text, and drops
  // a section entirely once nothing in it matches, so the contents list stays
  // in step with what is on screen.
  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();

    return cbqSections
      .map((section) => ({
        ...section,
        questions: q
          ? section.questions.filter(([question, answer]) =>
              `${question} ${answer}`.toLowerCase().includes(q),
            )
          : section.questions,
      }))
      .filter((section) => section.questions.length > 0)
      .map(({ title, questions }) => ({
        title,
        body: (
          <div className="bl-qa">
            {questions.map(([question, answer]) => (
              <div key={question}>
                <h3 className="bl-qa__q">{question}</h3>
                <div className="bl-doc__prose">
                  <ReactMarkdown>{answer}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        ),
      }));
  }, [query]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TOTAL_QUESTIONS;
    return cbqSections.reduce(
      (n, section) =>
        n +
        section.questions.filter(([question, answer]) =>
          `${question} ${answer}`.toLowerCase().includes(q),
        ).length,
      0,
    );
  }, [query]);

  const toolbar = (
    <div className="bl-doc__filter">
      <div className="bl-field">
        <label
          className="bl-field__label"
          htmlFor="cbq-filter"
        >
          Search the questions
        </label>
        <input
          id="cbq-filter"
          className="bl-input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Try scanning, refund, privacy"
        />
      </div>
      <p
        className="bl-field__hint"
        aria-live="polite"
      >
        {query.trim()
          ? `${matches} of ${TOTAL_QUESTIONS} questions match`
          : `${TOTAL_QUESTIONS} questions across ${cbqSections.length} sections`}
      </p>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Common BLEEP Questions | BLEEP</title>
        <meta
          name="description"
          content="Answers to common questions about BLEEP, the BleST security tag, and what the platform does for consumers, brands, distributors and regulators."
        />
      </Helmet>

      <DocPage
        title="Common BLEEP Questions"
        lead="What BLEEP does, how the security tag works, and what it means for you."
        toolbar={toolbar}
        sections={sections}
        domain={domain}
      />
    </>
  );
};

export default CBQs;
